<template>
	<div class="range-container">
		<div v-if="showLabel" class="range-label start">
			<slot name="start"></slot>
		</div>
		<div
			class="custom-range"
			:class="{ dragging: draggingKnob !== null }"
			@mousedown.prevent="startDrag"
			@touchstart.prevent="startDrag"
			ref="rangeEl"
		>
			<div class="range-track"></div>

			<div class="range-active-track" :style="activeTrackStyle"></div>

			<div
				v-for="(val, idx) in knobValues"
				:key="idx"
				class="range-knob"
				:style="getKnobStyle(val)"
				@mousedown.stop="startKnobDrag(idx)"
				@touchstart.stop="startKnobDrag(idx)"
			>
				<div v-if="showPin && draggingKnob === idx" class="range-pin">{{ Math.round(val) }}</div>
			</div>
		</div>
		<div v-if="showLabel" class="range-label end">
			<slot name="end"></slot>
		</div>
	</div>
</template>
<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue';

const props = withDefaults(
	defineProps<{
		modelValue?: number | number[];
		min?: number;
		max?: number;
		step?: number;
		showPin?: boolean;
		showLabel?: boolean;
	}>(),
	{
		min: 0,
		max: 100,
		step: 1,
		showPin: true,
		showLabel: true,
	}
);

const emit = defineEmits<{
	(e: 'update:modelValue', value: number | number[]): void;
}>();

const rangeEl = ref();
const draggingKnob = ref<number | null>(null);

const toKnobValues = (val: number | number[] | undefined): number[] => {
	if (Array.isArray(val)) return [...val];
	if (typeof val === 'number') return [val];
	return [];
};

const knobValues = ref<number[]>(toKnobValues(props.modelValue));

watch(toRef(props, 'modelValue'), val => {
	knobValues.value = toKnobValues(val);
});

const activeTrackStyle = computed(() => {
	const values = [...knobValues.value].sort((a, b) => a - b);
	const range = props.max - props.min;

	if (values.length === 1) {
		const end = (((values[0] ?? props.min) - props.min) / range) * 100;
		return { left: '0%', width: `${end}%` };
	} else {
		const start = (((values[0] ?? props.min) - props.min) / range) * 100;
		const end = (((values[values.length - 1] ?? props.max) - props.min) / range) * 100;
		return { left: `${start}%`, width: `${end - start}%` };
	}
});

const getKnobStyle = (val: number) => {
	const percent = ((val - props.min) / (props.max - props.min)) * 100;
	// clamp keeps the knob fully within the track at both edges (accounts for the -50% x-translate)
	return { left: `clamp(6px, ${percent}%, calc(100% - 6px))` };
};

const updateValueFromPosition = (x: number) => {
	const rect = rangeEl.value.getBoundingClientRect();
	const percent = Math.min(Math.max((x - rect.left) / rect.width, 0), 1);
	const rawVal = props.min + percent * (props.max - props.min);
	return Math.round(rawVal / props.step) * props.step;
};

const onDrag = (e: any) => {
	if (draggingKnob.value === null) return;
	const clientX = e.touches ? e.touches[0].clientX : e.clientX;
	const newVal = updateValueFromPosition(clientX);

	knobValues.value[draggingKnob.value] = newVal;
	knobValues.value = [...knobValues.value].sort((a, b) => a - b);
};

const stopDrag = () => {
	document.removeEventListener('mousemove', onDrag);
	document.removeEventListener('mouseup', stopDrag);
	document.removeEventListener('touchmove', onDrag);
	document.removeEventListener('touchend', stopDrag);

	const emittedValue: number | number[] =
		knobValues.value.length === 1 ? knobValues.value[0] ?? props.min : [...knobValues.value];
	emit('update:modelValue', emittedValue);

	draggingKnob.value = null;
	document.body.style.cursor = 'default';
};

const startKnobDrag = (index: number) => {
	draggingKnob.value = index;
	document.addEventListener('mousemove', onDrag);
	document.addEventListener('mouseup', stopDrag);
	document.addEventListener('touchmove', onDrag);
	document.addEventListener('touchend', stopDrag);
	document.body.style.setProperty('cursor', 'grabbing', 'important');
};

const startDrag = (e: any) => {
	if (draggingKnob.value !== null) return;

	const clientX = e.touches ? e.touches[0].clientX : e.clientX;
	const newVal = updateValueFromPosition(clientX);

	let nearestIndex = 0;
	let minDist = Infinity;
	knobValues.value.forEach((v, i) => {
		const dist = Math.abs(v - newVal);
		if (dist < minDist) {
			minDist = dist;
			nearestIndex = i;
		}
	});

	knobValues.value[nearestIndex] = newVal;
	knobValues.value.sort((a, b) => a - b);

	startKnobDrag(nearestIndex);
};
</script>
<style scoped lang="scss">
.range-container {
	display: flex;
	align-items: center;
	padding: 0 14px 8px;

	.range-label {
		font-size: 12px;
		color: rgb(213, 213, 213);

		&.start {
			margin-inline-end: 20px;
		}
		&.end {
			margin-inline: 14px 0px;
		}
	}

	.custom-range {
		position: relative;
		flex-grow: 1;
		height: 42px;
		background: transparent;
		touch-action: none;
		user-select: none;
		cursor: grab;

		&.dragging {
			cursor: grabbing !important;
		}

		.range-track {
			position: absolute;
			top: 50%;
			height: 2px;
			background: rgba(76, 90, 103, 0.26);
			width: 100%;
			border-radius: 2px;
			transform: translateY(-50%);
		}

		.range-active-track {
			position: absolute;
			top: 50%;
			height: 2px;
			background: #4c5a67;
			border-radius: 2px;
			transform: translateY(-50%);
		}

		.range-knob {
			position: absolute;
			top: 50%;
			transform: translate(-50%, -50%);
			background: #4c5a67;
			width: 12px;
			height: 12px;
			border-radius: 50%;

			.range-pin {
				position: absolute;
				top: -35px;
				left: 50%;
				transform: translateX(-50%);
				color: #fff;
				font-size: 12px;
				white-space: nowrap;

				&::before {
					left: 50%;
					top: -8px;
					margin-left: -16px;
					border-radius: 50% 50% 50% 0;
					position: absolute;
					width: 32px;
					height: 32px;
					transform: rotate(-45deg);
					background: #4c5a67;
					content: '';
					z-index: -1;
				}
			}
		}
	}
}
</style>
